import React, { useState, useEffect, useRef } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import promiseMemoize from 'promise-memoize'
import { FaPlus, FaFileExcel, FaFileImport } from 'react-icons/fa'
import { IconContext } from 'react-icons'

// Components
import { StatusBanner } from '../_components/StatusBanner'
import { ErrorGroup } from '../_components/ErrorGroup'
import { Button } from '../_components/Button'
import { FormField } from '../_components/Forms/FormField'
import { StatList } from '../_components/StatList'
import { ControlsButton } from '../_components/ControlsButton'
import { EditableTable } from '../_components/EditableTable'
import { SectionLoader } from '../_components/SectionLoader'
import { FilesManager } from '../_components/FilesManager'
import { ClipLoader } from 'react-spinners'
import { UploadResults } from '../_views/ClientViews/UploadInvoices/UploadResults'

// Styled Components
import { S_InvoicesOverview_TableControls } from '../_views/ClientViews/UploadInvoices/S_UploadInvoices'
import { S_ClientInvoicesCreate } from './S_ClientInvoiceCreate'

// Services
import {
  submitInvoiceSchedule,
  updateInvoiceSchedule,
  createInvoiceForSchedule,
  deleteInvoice,
  getInvoiceSchedule,
  getScheduleInvoices,
  updateInvoice,
  createEmptyInvoiceSchedule,
} from '../_services/lender.service'
import { uploadInvoiceScheduleDocument, searchDebtors } from '../_services/lender.service'
import { getDebtor } from '../_services/debtor.service'

//Helpers
import { history } from '../_helpers'
import { createSearchParams } from '../_helpers/search'
import { currencyToNum } from '../_helpers/formatting'
import { getDateInputFormat, dateFromInputFormat, systemNow } from '../_helpers/dates'
import { columnsToTemplate } from '../_helpers/SpreadSheetUtils'
import { isFeatureEnabled } from '../_helpers/flags'
import { useBusiness } from '../_reduxHooks/business.hook'
import { uploadInvoiceSchedule } from '../_services/invoices.service'
import { useFlags } from '../_reduxHooks/flags.hook'
import { modalActionHooks, notificationActionHooks } from '../_actionHooks'
import ROUTES from '../_constants/routes'

const debtorValueFetch = promiseMemoize(getDebtor)

const EDITABLE_STATES = ['incomplete']

const fetchDebtorSuggestions = (searchText) => {
  let searchParams = createSearchParams({
    searchTerm: searchText,
    searchParam: 'debtor_name',
  })
  searchParams.set('type', 'tenant')
  return searchDebtors(searchParams.toString())
}

const minifyInvoices = (invoices) => {
  return invoices.map((invoice) => {
    const { id, debtor_id, invoice_number, invoice_date, payment_terms, face_value, po_number } = invoice
    return {
      id,
      debtor_id,
      invoice_number,
      po_number,
      invoice_date: getDateInputFormat(invoice_date),
      payment_terms,
      face_value: Number(face_value),
    }
  })
}

// Returns all duplicate errors in a set of invoices
const getDuplicateErrors = (invoices) => {
  const duplicateErrors = []
  invoices.forEach((invoice) => {
    if (invoice) {
      const { id, errors } = invoice
      if (errors && errors['invoice_number'] && errors['invoice_number'] === 'duplicate invoice number') {
        duplicateErrors.push(id)
      }
    }
  })
  return duplicateErrors
}
//with set schedule invoices being async we need to keep track of what the schedule invoices are synchronously so that we don't PUT incorrect data to the server
let synchronousInvoices = []

export const ClientInvoiceCreate = ({ scheduleSubmitted = () => {}, match }) => {
  // State
  const [overallErr, setOverallErr] = useState('')
  const [schedule, setSchedule] = useState(undefined)
  const [isEditable, setIsEditable] = useState(true)
  const [loadingSchedule, setLoadingSchedule] = useState(false)
  const [hasLoadedSchedule, setHasLoadedSchedule] = useState(false)
  const businessIdRef = useRef(null)
  const fileInput = useRef(null)
  const [expectedBatchTotal, setExpectedBatchTotal] = useState(0)
  const [businessId, setBusinessId] = useState(null)
  const [scheduleInvoices, setScheduleInvoices] = useState([])
  const [duplicateErrors, setDuplicateErrors] = useState([])
  const [addingNewInvoice, setAddingNewInvoice] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState()
  const [documents, setDocuments] = useState([])
  const [uploadingDocs, setUploadingDocs] = useState(false)
  const { business } = useBusiness()
  const { featureFlags } = useFlags()
  const openModal = modalActionHooks.useOpenModal()
  const showNotification = notificationActionHooks.useShowNotification()
  const columns = [
    {
      id: 'debtor_id',
      label: 'Debtor',
      type: 'autocomplete',
      mobileMaxWidth: 100,
      validate: (id) => {
        if (!id) {
          return 'Debtor is Required'
        }
        return null
      },
      autoCompleteProps: {
        valueIdAttr: 'id',
        valueLabelAttr: 'debtor_name',
        fetchRecord: debtorValueFetch,
        limitMobileHeight: true,
        fetchSuggestions: fetchDebtorSuggestions,
      },
      inSpreadsheet: true,
      spreadSheetColumns: ['Debtor ID', 'Debtor Name'],
    },
    {
      id: 'invoice_number',
      label: 'Invoice #',
      type: 'text',
      mobileMaxWidth: 70,
      validate: (value) => {
        if (!value) {
          return 'Invoice # is Required'
        }
        return null
      },
      inSpreadsheet: true,
    },
    {
      id: 'po_number',
      label: 'Purchase Order',
      type: 'text',
      mobileMaxWidth: 120,
      validate: (value) => {
        return null
      },
      inSpreadsheet: true,
    },
    {
      id: 'invoice_date',
      label: 'Invoice Date',
      type: 'date',
      mobileMaxWidth: 150,
      validate: (datestring) => {
        if (!datestring) {
          return 'Invoice Date is Required'
        }
        let dateParts = datestring.split('-')
        let year = Number(dateParts[0])
        let month = Number(dateParts[1]) - 1
        let day = Number(dateParts[2])
        let dateForCompare = new Date(year, month, day)
        let now = systemNow()
        if (now.getTime() < dateForCompare.getTime()) {
          return 'Invoice Date must be in the past'
        }
        if (business?.max_invoice_age_for_purchase) {
          let differenceMs = now.getTime() - dateForCompare.getTime()
          let age = Math.ceil(differenceMs / (1000 * 60 * 60 * 24))
          if (age > business.max_invoice_age_for_purchase) {
            return `Invoice is over age limit (${business.max_invoice_age_for_purchase}).`
          }
        }
        return ''
      },
      inSpreadsheet: true,
    },
    {
      id: 'payment_terms',
      label: 'Terms',
      type: 'integer',
      defaultValue: 30,
      mobileMaxWidth: 50,
      max: business?.max_invoice_term || 120,
      validate: (num) => {
        if (!num) {
          return 'Due Date is required'
        }
        if (num <= 0) {
          return 'Due Date must be greater than 0'
        }
        if (business.max_invoice_term && num > business.max_invoice_term) {
          return `Invoice term is over max allowed (${business.max_invoice_term}).`
        }
        return ''
      },
      inSpreadsheet: true,
    },
    {
      id: 'face_value',
      label: 'Amount',
      type: 'currency',
      mobileMaxWidth: 100,
      validate: (currencyString) => {
        let num = typeof currencyString == 'string' ? currencyToNum(currencyString || '$0.00') : currencyString
        if (!(num > 0)) {
          return 'Invoice Amount must be greater than 0'
        }
        return ''
      },
      inSpreadsheet: true,
    },
  ]

  useEffect(() => {
    setIsEditable(schedule && EDITABLE_STATES.includes(schedule.state))
  }, [schedule])

  const loadSchedule = () => {
    Promise.all([getInvoiceSchedule(scheduleId), getScheduleInvoices(scheduleId, 'count=10000')]).then((vals) => {
      const schedule = vals[0]
      const invoices = vals[1]
      const duplicateErrors = getDuplicateErrors(invoices)
      const minifiedInvoices = minifyInvoices(invoices)
      unstable_batchedUpdates(() => {
        setBusinessId(schedule.business_id)
        setExpectedBatchTotal(schedule.expected_total_value)
        setSchedule(schedule)
        setDocuments(schedule.documents)
        setScheduleInvoices(minifiedInvoices)
        synchronousInvoices = minifiedInvoices
        setDuplicateErrors(duplicateErrors)
        setLoadingSchedule(false)
        setHasLoadedSchedule(true)
        if (schedule.expected_total_value) {
          setExpectedBatchTotal(schedule.expected_total_value)
        }
      })
    })
  }

  const cancelEditingOnError = (error = 'Something went wrong. Please reload the page and try again.') => {
    showNotification({
      type: `error`,
      message: error,
      id: `create-invoice-fail`,
    })
    loadSchedule()
    return false
  }

  const scheduleId = match.params.schedule_id
  const newSchedule = scheduleId === 'create'
  // Focus the business field on load
  useEffect(() => {
    if (businessIdRef.current && !businessId) {
      businessIdRef.current.focus()
    }
    synchronousInvoices = scheduleInvoices
  })

  // Get the schedule and invoices for the schedule
  useEffect(() => {
    if (scheduleId && schedule == undefined && !loadingSchedule && !newSchedule) {
      setLoadingSchedule(true)
      loadSchedule()
    }
  })

  // Create invoice and add it to the schedule
  const addInvoice = async (invoiceObj = null, updateState = true) => {
    if (updateState) {
      setAddingNewInvoice(true)
    }
    const defaultDebtorDueDate = new Date()
    defaultDebtorDueDate.setDate(defaultDebtorDueDate.getDate() + 30)
    let saveInvoice = {
      business_id: businessId,
      invoice_date: getDateInputFormat(new Date()),
      debtor_due_date: getDateInputFormat(defaultDebtorDueDate),
      payment_terms: 90,
      face_value: 0, // Needs a face value set to add to schedule
    }
    if (invoiceObj) {
      saveInvoice = {
        ...invoiceObj,
        business_id: businessId,
      }
    }

    const newInvoice = await createInvoiceForSchedule({
      invoice: saveInvoice,
      invoice_schedule_id: scheduleId,
    }).catch((error) => {
      return cancelEditingOnError(error)
    })

    const newInvoices = [...scheduleInvoices]
    const minifiedInvoices = minifyInvoices([newInvoice])
    newInvoices.push(minifiedInvoices[0])
    if (updateState) {
      synchronousInvoices = newInvoices
      unstable_batchedUpdates(() => {
        setScheduleInvoices(newInvoices)
        setAddingNewInvoice(false)
      })
    }

    return saveInvoice
  }

  // Update Invoice
  const updateInvoices = (entityId, fieldId, newValue) => {
    const newInvoices = scheduleInvoices.map((invoice) => {
      const newInvoice = { ...invoice }
      if (newInvoice.id === entityId) {
        newInvoice[fieldId] = newValue
        if (fieldId === 'payment_terms' || fieldId === 'invoice_date') {
          if (newInvoice.payment_terms && fieldId === 'invoice_date') {
            const invoiceDate = dateFromInputFormat(newValue)
            const toAdd = Number(newInvoice.payment_terms)
            invoiceDate.setDate(invoiceDate.getDate() + toAdd)
            newInvoice.debtor_due_date = getDateInputFormat(invoiceDate)
          } else if (newInvoice.invoice_date && fieldId === 'payment_terms') {
            const invoiceDate = dateFromInputFormat(newInvoice.invoice_date)
            const toAdd = Number(newValue)
            invoiceDate.setDate(invoiceDate.getDate() + toAdd)
            newInvoice.debtor_due_date = getDateInputFormat(invoiceDate)
          }
        }
      }
      return newInvoice
    })
    synchronousInvoices = newInvoices
    // Remove duplicate invoice number errors
    if (fieldId === 'invoice_number') {
      const newDuplicateErrors = duplicateErrors.filter((invoiceId) => invoiceId !== entityId)
      unstable_batchedUpdates(() => {
        setScheduleInvoices(newInvoices)
        setDuplicateErrors(newDuplicateErrors)
      })
    } else {
      setScheduleInvoices(newInvoices)
    }
  }

  // Saves changes to the invoice using the service call
  const saveInvoice = (entityId, fieldId) => {
    const matches = synchronousInvoices.filter((invoice) => {
      return invoice.id === entityId
    })
    if (matches && matches.length) {
      const invoiceToSave = matches[0]
      const updateData = { [fieldId]: invoiceToSave[fieldId] }
      if (fieldId === 'payment_terms' || fieldId === 'invoice_date') {
        updateData.debtor_due_date = invoiceToSave.debtor_due_date
      }
      updateInvoice(entityId, updateData)
        .then((updatedInvoice) => {
          // Update duplicates as needed
          const newDuplicates = getDuplicateErrors([updatedInvoice])
          if (newDuplicates) {
            const newDuplicateErrors = [...duplicateErrors, newDuplicates[0]]
            setDuplicateErrors(newDuplicateErrors)
          }
        })
        .catch((error) => {
          return cancelEditingOnError(error)
        })
    }
  }

  // Remove an entity from the list, also remove duplicates
  const removeInvoice = (entityId) => {
    const newInvoices = scheduleInvoices.filter((invoice) => {
      return invoice.id !== entityId
    })
    const newDuplicateErrors = duplicateErrors.filter((invoiceId) => invoiceId !== entityId)
    unstable_batchedUpdates(() => {
      setScheduleInvoices(newInvoices)
      setDuplicateErrors(newDuplicateErrors)
    })
    // Delete invoice on the server
    deleteInvoice(entityId)
  }

  // Build errors - rebuild all of them every time
  let invoiceErrors = {}
  let hasInvoiceErrors = false
  scheduleInvoices.forEach((invoice) => {
    const newInvoiceErrors = {}
    Object.keys(invoice).forEach((key) => {
      const fieldValue = invoice[key]

      newInvoiceErrors[key] = columns.find((col) => col.id === key)?.validate?.(fieldValue)
      if (newInvoiceErrors[key]) {
        hasInvoiceErrors = true
      }
    })

    invoiceErrors[invoice.id] = newInvoiceErrors
  })

  // Account for duplicate errors
  duplicateErrors.forEach((invoiceId) => {
    if (invoiceErrors[invoiceId]) {
      invoiceErrors[invoiceId]['invoice_number'] = 'Duplicate Invoice #'
      hasInvoiceErrors = true
    }
  })

  // Add up dynamic batch total
  // - Uses toFixed to avoid JS floating point bugs
  let batchTotal = scheduleInvoices.reduce((batchTotal, currentInvoice) => {
    return batchTotal + Number(Number(currentInvoice.face_value).toFixed(2))
  }, 0)
  batchTotal = Number(batchTotal.toFixed(2))

  // Can user submit - based on all error types
  // TODO include errors from expected batch total, others
  const canSubmit = () => {
    let toReturn =
      !loadingSchedule &&
      scheduleInvoices &&
      scheduleInvoices.length > 0 &&
      !hasInvoiceErrors &&
      Number(batchTotal).toFixed(2) == Number(expectedBatchTotal).toFixed(2) &&
      documents.length //this is client side only
    if (!scheduleInvoices || scheduleInvoices.length == 0) {
      setOverallErr('Please add at least 1 invoice to this schedule before submitting')
    } else if (hasInvoiceErrors) {
      setOverallErr('Please fix all invoice-specific errors prior to submitting')
    } else if (Number(batchTotal).toFixed(2) != Number(expectedBatchTotal).toFixed(2)) {
      setOverallErr(`Batch Total does not match expected batch total: ${expectedBatchTotal}`)
    } else if (!documents.length) {
      setOverallErr('Please review and ensure supporting documentation for these invoices have been uploaded.')
    } else {
      setOverallErr('')
    }
    return toReturn
  }

  const renderUploadControls = () => {
    if (!isFeatureEnabled('upload_invoices_enabled', featureFlags)) return null
    return (
      <>
        <Button
          className={`table-controls-btn compact`}
          onClick={() => {
            columnsToTemplate(
              columns.map((c) => {
                c.headingText = c.label
                return c
              })
            )
          }}
        >
          <IconContext.Provider value={{ size: '16px', className: 'add-icon' }}>
            <FaFileExcel />
          </IconContext.Provider>
          Need Template?
        </Button>
        <input
          accept=".xlsx, .csv"
          onChange={() => {
            setLoadingSchedule(true)
            // Prevent firing if no file found
            if (!fileInput.current.files || !fileInput.current.files.length) {
              return
            }
            uploadInvoiceSchedule(fileInput.current.files[0], scheduleId)
              .then((data) => {
                openModal(UploadResults, {
                  successCount: data.success_count,
                  errors: data.errors,
                  warnings: data.warnings,
                })
                loadSchedule()
                setLoadingSchedule(false)
              })
              .catch((error) => {
                openModal(UploadResults, {
                  successCount: 0,
                  errors: [],
                  warnings: [],
                  templateError: error,
                })
                loadSchedule()
                setLoadingSchedule(false)
              })
          }}
          ref={fileInput}
          style={{ display: 'none' }}
          type="file"
        />
        <Button
          className={`table-controls-btn compact`}
          onClick={() => {
            fileInput.current.click()
          }}
        >
          <IconContext.Provider value={{ size: '16px', className: 'add-icon' }}>
            <FaFileImport />
          </IconContext.Provider>
          Upload Invoices
        </Button>
      </>
    )
  }

  return (
    <S_ClientInvoicesCreate>
      <Button buttonType="link" className="link" to={ROUTES.client_home}>
        Back to summary
      </Button>
      <div className={`business-selection`}>
        <div className="left">
          <StatList
            items={[
              {
                label: 'Batch Total',
                type: null,
                tooltip: 'Manually enter schedule total to ensure balance is same as auto-calculated schedule total.',
                value: (
                  <FormField
                    disabled={schedule && !isEditable}
                    onBlur={async () => {
                      if (scheduleId && !newSchedule) {
                        await updateInvoiceSchedule(scheduleId, {
                          expected_total_value: expectedBatchTotal,
                        })
                      }
                    }}
                    onChange={(numVal) => {
                      setExpectedBatchTotal(numVal)
                    }}
                    type="currency"
                    value={expectedBatchTotal}
                  />
                ),
              },
              {
                label: '',
                type: null,
                value: newSchedule && (
                  <Button
                    className={`compact save-client-btn`}
                    onClick={async () => {
                      if (business.id) {
                        let schedule = await createEmptyInvoiceSchedule(business.id)
                        if (expectedBatchTotal) {
                          await updateInvoiceSchedule(schedule.id, {
                            expected_total_value: expectedBatchTotal,
                          })
                        }
                        history.push(ROUTES.client_invoice_submission + `/${schedule.id}`, {
                          business_id: business.id,
                        })
                      } else {
                        setOverallErr('Please Select a Client')
                      }
                    }}
                  >
                    Create
                  </Button>
                ),
              },
              scheduleInvoices &&
                scheduleInvoices.length && {
                  label: 'Invoices Total',
                  type: 'currency',
                  value: batchTotal,
                },
            ]}
            layout="summary"
          />
        </div>
        {/* Show Errors */}
        <div className="right">
          {overallErr && <StatusBanner className={`err-banner`} icon="!" message={overallErr} />}
          <div className={`btn-wrapper`}>
            {hasInvoiceErrors && (
              <Button
                className={`compact secondary table-errs`}
                onClick={() => {
                  let errorsList = []
                  Object.keys(invoiceErrors).forEach((key, i) => {
                    let errorsForInvoice = invoiceErrors[key]
                    Object.keys(errorsForInvoice).forEach((key, j) => {
                      let fieldError = errorsForInvoice[key]
                      if (fieldError) {
                        errorsList.push(`Row ${i + 1}: ${fieldError}`)
                      }
                    })
                  })
                  if (errorsList.length > 0) {
                    openModal(ErrorGroup, { errors: errorsList, title: 'Invoice Errors' })
                  } else {
                    openModal(() => {
                      return <div>Looking good! No more Invoice Errors Detected.</div>
                    })
                  }
                }}
              >
                Show Errors
              </Button>
            )}
            {/* Save Button */}
            {schedule && isEditable && !loadingSchedule && (
              <Button
                className={`compact table-errs`}
                onClick={async (e) => {
                  if (canSubmit()) {
                    try {
                      let schedule = await submitInvoiceSchedule(scheduleId)
                      scheduleSubmitted(await getInvoiceSchedule(scheduleId))
                      history.push('/client_home')
                    } catch (e) {
                      if (e.data.invoice_schedule_errors) {
                        setOverallErr(e.data.invoice_schedule_errors[Object.keys(e.data.invoice_schedule_errors)[0]])
                      }
                      if (e.data.invoice_errors) {
                        let newInvoiceErrors = {}
                        e.data.invoice_errors.map((err) => {
                          newInvoiceErrors[err.invoice_id] = err.errors
                        })
                        invoiceErrors = newInvoiceErrors
                        //force re-render of table
                        setScheduleInvoices(scheduleInvoices)
                      }
                    }
                  }
                }}
              >
                Submit
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Editable Table and Controls*/}
      {scheduleId && loadingSchedule && <SectionLoader />}
      {(newSchedule || (scheduleId && scheduleInvoices && hasLoadedSchedule)) && (
        <>
          {/* Table */}
          <EditableTable
            RowControls={
              isEditable
                ? ({ entityId }) =>
                    scheduleInvoices &&
                    scheduleInvoices.length > 0 && (
                      <ControlsButton
                        icon="close"
                        onClick={() => {
                          removeInvoice(entityId)
                        }}
                      />
                    )
                : null
            }
            entities={scheduleInvoices}
            errors={invoiceErrors}
            formFields={columns}
            minWidth={680}
            onBlur={saveInvoice}
            onChange={updateInvoices}
            readOnly={!isEditable}
            scrollOnMobile={true}
          />

          {/* Controls */}
          {schedule && isEditable && (
            <S_InvoicesOverview_TableControls>
              {renderUploadControls()}
              <Button
                className={`table-controls-btn compact`}
                icon="add"
                isLoading={addingNewInvoice}
                onClick={() => addInvoice()}
              >
                <IconContext.Provider value={{ size: '16px', className: 'add-icon' }}>
                  {addingNewInvoice ? (
                    <ClipLoader color={`white`} size={16} />
                  ) : (
                    <>
                      <FaPlus />
                      {`New Invoice`}
                    </>
                  )}
                </IconContext.Provider>
              </Button>
            </S_InvoicesOverview_TableControls>
          )}

          {/* Testing */}
          {/* <pre>{JSON.stringify(scheduleInvoices, null, 1)}</pre>
                        <pre>{JSON.stringify(invoiceErrors, null, 1)}</pre> */}
        </>
      )}

      <FilesManager
        allowDownload={false}
        allowMultiple={true}
        disabled={newSchedule}
        files={documents}
        isUploading={uploadingDocs}
        onDelete={() => {
          return getInvoiceSchedule(scheduleId).then((resp) => {
            unstable_batchedUpdates(() => {
              setDocuments(resp.documents.filter((d) => !d.deleted))
            })
          })
        }}
        onFilesAccepted={async (files) => {
          setUploadingDocs(true)
          await Promise.all(files.map((f) => uploadInvoiceScheduleDocument(scheduleId, f)))
          getInvoiceSchedule(scheduleId).then((resp) => {
            unstable_batchedUpdates(() => {
              setDocuments(resp.documents.filter((d) => !d.deleted))
              setUploadingDocs(false)
            })
          })
        }}
        title={`Supporting Documents`}
        viewType={'list'}
      />
    </S_ClientInvoicesCreate>
  )
}
